import role1Schema from '../../../../assets/forms/role-1.form.json';
import typesDict from '../../../../assets/types.dict.json';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildWarningBag,
  sheetHasTriggeredWarning,
  WarnableControl,
} from './warning-eval.helper';
import {
  checkCurrentEndFloor,
  checkCurrentRange,
  checkDateSequence,
  checkPreviousRange,
  deriveDisabledKeys,
  deriveStaticRequiredKeys,
  DisableDerivableControl,
  FILING_INFO_CONDITIONAL_RULES,
  FILING_INFO_FIELD_KEYS,
} from './filing-info-common.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Pure validator for Role 1 (Filing Information).
 *
 * Mirrors the editor's role1-010000-filing-info.component.ts validation
 * surface:
 *
 *  - Static required: fields where buildForm() attaches Validators.required —
 *    i.e. unconditionally required via EITHER `control.required === true` OR
 *    a `validators: [{ name: 'required' }]` entry without a conditionalOn
 *    property. Mirrors the expanded isUnconditionallyRequired check applied
 *    to buildForm() to fix the same latent bug originally found on Role 6
 *    (pre-fix, only the top-level flag was honoured — the validators-array
 *    required entry was silently dropped, e.g. for
 *    ssmt-dei:DescriptionOfFunctionalCurrency which carries `required: false`
 *    together with `validators: [{ name: 'required' }]`).
 *  - Pattern validators: every control whose validators array has a
 *    `{ name: 'pattern', patternSource: 'type:<key>' }` entry with a matching
 *    regex in types.dict.json `patterns`. Mirrors the buildForm() branch that
 *    compiles `Validators.pattern(typeDict.patterns[key])`.
 *  - 5 conditional required rules — see FILING_INFO_CONDITIONAL_RULES in the
 *    adjacent rules file. These match setupConditionalValidation() 1:1:
 *      ApplicationOfSubmission === 'Rectification filing'
 *        -> StatutoryDeclarationForRectification required
 *      ApplicationOfSubmission in {Court order filing,
 *                                  Rectification filing with court order}
 *        -> CourtOrderReferenceNumber required
 *      FinancialStatementsPreparation === 'Subsequent preparation of
 *                                          financial statements'
 *        -> CompanyPreviousFinancialYear(Start|End)Date required
 *      AuditStatus === 'Unaudited'
 *        -> AuditExemptionCategory required
 *      AccountingStandard === 'Others'
 *        -> DisclosureOfOtherAccountingStandardsApplied required
 *
 * Cross-field date validation:
 *  - The Current/Previous FinancialYear start/end pairs are constrained by
 *    the three shared check* primitives in filing-info-common.rules.ts
 *    (current range, previous range, previous-end-before-current-start
 *    sequence). The editor wires these through
 *    setupFinancialYearDateValidation(), so this validator mirrors them via
 *    checkDateErrors() to keep the headless verdict aligned with `form.valid`.
 *    (Historically role1 was dead and Phase 2 rendered via role0; when role1
 *    was revived this wiring was missed on both the editor and this validator,
 *    so the date errors silently stopped surfacing in Phase 2 — now restored.)
 *
 *  - Disabled-field exclusion: a control that is `ui.readonly`, or whose
 *    `ui.disabledWhen` rule currently matches, is DISABLED in the editor and
 *    Angular fires none of its validators — so neither required nor pattern may
 *    flag it here either. This header used to claim the opposite ("transparent to
 *    required/pattern checks"), which held for the validator ARRAY but not for the
 *    verdict, letting the headless listing publish a ✗ the editor never showed.
 *    See deriveDisabledKeys in filing-info-common.rules.ts.
 *
 * Not mirrored (intentional):
 *  - `autoManageByTypeOfSubmission` / `autoManageByContext` visibility logic:
 *    these mutate field values but neither disable the control nor add/remove
 *    validators, so they are transparent once the form value snapshot
 *    (getRawValue) is persisted to FilingFormState.
 *
 * Cross-sheet reads: none. The component only reads its own sheet via
 * stateService.getSheetData('filingInfo'). Filing info is itself the SOURCE
 * of submissionType / accountingStandard for other sheets' contexts — so
 * this validator does not need context.crossSheetData.
 */

interface SchemaValidator {
  name?: string;
  patternSource?: string;
  pattern?: string;
  other?: string;
  conditionalOn?: unknown;
}

interface SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: SchemaValidator[];
  ui?: { control?: string; readonly?: boolean };
}

const SCHEMA_CONTROLS: ReadonlyArray<SchemaControl> =
  role1Schema.controls as SchemaControl[];

/**
 * Static required keys — fields where buildForm() adds Validators.required.
 * Derived via the shared `deriveStaticRequiredKeys` so the editor's
 * buildForm(), this validator, and the role0 paths all apply the identical
 * "required === true OR an unconditional validators:[{required}] entry" rule.
 * (See the latent-bug note in the file header and in filing-info-common.rules.)
 */
const STATIC_REQUIRED_KEYS: ReadonlySet<string> =
  deriveStaticRequiredKeys(SCHEMA_CONTROLS);

/**
 * The same control list viewed through the disable lens. Kept as a module
 * constant so `deriveDisabledKeys` only re-evaluates the `disabledWhen`
 * predicates (which depend on sheetData) per call, not the cast.
 */
const DISABLE_DERIVABLE_CONTROLS =
  role1Schema.controls as unknown as ReadonlyArray<DisableDerivableControl>;

/**
 * (qname -> compiled RegExp) for fields with a pattern validator. Compiled
 * once at module load so per-validate calls are cheap.
 */
const PATTERN_BY_KEY: ReadonlyMap<string, RegExp> = (() => {
  const map = new Map<string, RegExp>();
  const patterns =
    (typesDict as { patterns?: Record<string, string> }).patterns ?? {};
  for (const control of SCHEMA_CONTROLS) {
    const patternValidator = control.validators?.find((validator) => validator.name === 'pattern');
    if (patternValidator?.patternSource) {
      const patternKey = patternValidator.patternSource.replace('type:', '');
      const pattern = patterns[patternKey];
      if (pattern) {
        try {
          map.set(control.qname, new RegExp(pattern));
        } catch {
          // Invalid regex in types dict — component would also fail to apply
          // this validator, so skipping preserves parity.
        }
      }
    }
  }
  return map;
})();

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function effectiveRequiredKeys(sheetData: Record<string, any>): Set<string> {
  const keys = new Set<string>(STATIC_REQUIRED_KEYS);
  for (const rule of FILING_INFO_CONDITIONAL_RULES) {
    if (rule.whenAnyOf.includes(sheetData[rule.trigger])) {
      for (const field of rule.requireFields) keys.add(field);
    }
  }
  // A disabled control fires no validators in the editor — see
  // deriveDisabledKeys in filing-info-common.rules.ts.
  for (const key of deriveDisabledKeys(DISABLE_DERIVABLE_CONTROLS, sheetData)) {
    keys.delete(key);
  }
  return keys;
}

function checkRequiredErrors(
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const key of effectiveRequiredKeys(sheetData)) {
    if (isEmpty(sheetData[key])) {
      errors[key] = `${key} is required`;
    }
  }
  return errors;
}

function checkPatternErrors(
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};
  const disabled = deriveDisabledKeys(DISABLE_DERIVABLE_CONTROLS, sheetData);
  for (const [key, pattern] of PATTERN_BY_KEY) {
    if (disabled.has(key)) continue;
    const value = sheetData[key];
    // Angular's Validators.pattern coerces to string and skips empty values.
    if (isEmpty(value)) continue;
    if (!pattern.test(String(value))) {
      errors[key] = `${key} has invalid format`;
    }
  }
  return errors;
}

/**
 * Cross-field date validation. Delegates to the shared check* primitives in
 * filing-info-common.rules.ts so the editor's Angular validator functions and
 * this pure validator emit identical messages (and identical keying onto the
 * start/previous-end fields). Mirrors role0's checkDateErrors() 1:1.
 */
function checkDateErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  const currentRange = checkCurrentRange(
    sheetData[FILING_INFO_FIELD_KEYS.currentYearStart],
    sheetData[FILING_INFO_FIELD_KEYS.currentYearEnd]
  );
  if (currentRange) errors[FILING_INFO_FIELD_KEYS.currentYearStart] = currentRange;

  const previousRange = checkPreviousRange(
    sheetData[FILING_INFO_FIELD_KEYS.previousYearStart],
    sheetData[FILING_INFO_FIELD_KEYS.previousYearEnd]
  );
  if (previousRange) errors[FILING_INFO_FIELD_KEYS.previousYearStart] = previousRange;

  const sequence = checkDateSequence(
    sheetData[FILING_INFO_FIELD_KEYS.previousYearEnd],
    sheetData[FILING_INFO_FIELD_KEYS.currentYearStart]
  );
  if (sequence) errors[FILING_INFO_FIELD_KEYS.previousYearEnd] = sequence;

  // Unsupported filing period: a current financial year END before
  // 2017-01-31 belongs to the "Financial Statements 1965" submission. Keyed
  // onto the END date itself (unlike the cross-field rules above, which key
  // onto the earlier of the pair) because it is a single-field constraint —
  // matching where the editor's `financialYearEndUnsupported` error lands.
  const currentEndFloor = checkCurrentEndFloor(
    sheetData[FILING_INFO_FIELD_KEYS.currentYearEnd]
  );
  if (currentEndFloor) errors[FILING_INFO_FIELD_KEYS.currentYearEnd] = currentEndFloor;

  return errors;
}

export const role1FilingInfoValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.filingInfo,
  // Tier 2: requiredIf / hideWhen against same-sheet fields (the 5
  // conditional rules). No cross-sheet reads — filingInfo is itself the
  // source of submissionType / accountingStandard that other sheets consume.
  tier: 2,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const errors: Record<string, string> = {
      ...checkRequiredErrors(sheetData),
      ...checkPatternErrors(sheetData),
      ...checkDateErrors(sheetData),
    };
    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: sheetHasTriggeredWarning(
        role1Schema.controls as unknown as WarnableControl[],
        sheetData,
        buildWarningBag(
          context.crossSheetData?.get(SHEET_KEYS.filingInfo) ??
            context.crossSheetData?.get(SHEET_KEYS.filingPrerequisites) ??
            {},
          sheetData
        )
      ),
      errors,
    };
  },
};
